import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Activity,
  Brain,
  Shield,
  CheckCircle2,
  Server,
  Users,
  RefreshCw,
  Settings,
  TrendingUp,
  Eye,
  Lock,
  ChevronRight,
} from 'lucide-react';
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { cn } from '../utils/cn'
import { formatRelativeTime } from '../utils/format'
import { MobileDistributedAgentOrchestration } from '../components/ai-mcp/mobile/MobileDistributedAgentOrchestration'
import { MobileMCPSecurityGateway } from '../components/ai-mcp/mobile/MobileMCPSecurityGateway'
import { MobileComplianceMonitor } from '../components/ai-mcp/mobile/MobileComplianceMonitor'

interface MCPServerMetrics {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'error' | 'starting';
  uptime: number;
  requestCount: number;
  errorRate: number;
  responseTime: number;
  memoryUsage: number;
  cpuUsage: number;
  connections: number;
  version: string;
  lastActivity: Date;
  capabilities: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'blocked';
  deployment: 'self-hosted' | 'aws-bedrock' | 'azure-openai' | 'public-api' | 'overseas';
  region?: string;
}

interface AIModelMetrics {
  id: string;
  name: string;
  provider: string;
  status: 'active' | 'idle' | 'error' | 'loading';
  requestsPerMinute: number;
  tokensProcessed: number;
  averageLatency: number;
  errorRate: number;
  costPerRequest: number;
  temperature: number;
  maxTokens: number;
  lastUsed: Date;
  riskLevel: 'low' | 'medium' | 'high' | 'blocked';
  deployment: 'self-hosted' | 'aws-bedrock' | 'azure-openai' | 'public-api' | 'overseas';
  region?: string;
  dataResidency: string;
}

// Empty data -- wire to API when backend is available
const generateMockMCPServers = (): MCPServerMetrics[] => [];

const generateMockAIModels = (): AIModelMetrics[] => [];

// Risk level utilities
const getRiskBadgeVariant = (riskLevel: string) => {
  switch (riskLevel) {
    case 'low': return 'success';
    case 'medium': return 'warning';
    case 'high': return 'danger';
    case 'blocked': return 'danger';
    default: return 'secondary';
  }
};

type MobileView = 'main' | 'mcp-servers' | 'ai-models' | 'orchestration' | 'security' | 'compliance';

export const MobileAIMCP: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial view from URL or default to 'main'
  const getInitialView = () => {
    const view = searchParams.get('view');
    const validViews = ['main', 'mcp-servers', 'ai-models', 'orchestration', 'security', 'compliance'];
    if (view && validViews.includes(view)) {
      return view as MobileView;
    }
    return 'main';
  };
  
  const [currentView, setCurrentView] = useState<MobileView>(getInitialView());
  
  // Update URL when view changes
  const handleViewChange = useCallback((view: MobileView) => {
    setCurrentView(view);
    setSearchParams({ view });
  }, [setSearchParams]);
  
  // Sync with URL changes
  useEffect(() => {
    const view = searchParams.get('view');
    const validViews = ['main', 'mcp-servers', 'ai-models', 'orchestration', 'security', 'compliance'];
    if (view && validViews.includes(view) && view !== currentView) {
      setCurrentView(view as MobileView);
    }
  }, [searchParams, currentView]);

  const mcpServers = generateMockMCPServers();
  const aiModels = generateMockAIModels();

  const overviewMetrics = {
    activeMCPServers: mcpServers.filter(s => s.status === 'running').length,
    totalMCPServers: mcpServers.length,
    activeAIModels: aiModels.filter(m => m.status === 'active').length,
    totalAIModels: aiModels.length,
    totalRequests: mcpServers.reduce((sum, server) => sum + server.requestCount, 0),
    avgCost: (aiModels.reduce((sum, model) => sum + model.costPerRequest, 0) / aiModels.length).toFixed(3),
  };

  const renderMainView = () => (
    <div className="space-y-4">
      {/* Status Cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Server className="w-5 h-5 text-success-500" />
              <Badge variant="success" className="text-xs">Active</Badge>
            </div>
            <div className="text-xl font-semibold text-console-text">
              {overviewMetrics.activeMCPServers}/{overviewMetrics.totalMCPServers}
            </div>
            <div className="text-xs text-console-muted">MCP Servers</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Brain className="w-5 h-5 text-primary-500" />
              <Badge variant="primary" className="text-xs">Active</Badge>
            </div>
            <div className="text-xl font-semibold text-console-text">
              {overviewMetrics.activeAIModels}/{overviewMetrics.totalAIModels}
            </div>
            <div className="text-xs text-console-muted">AI Models</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-warning-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              {overviewMetrics.totalRequests.toLocaleString()}
            </div>
            <div className="text-xs text-console-muted">Total Requests</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-accent-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              ${overviewMetrics.avgCost}
            </div>
            <div className="text-xs text-console-muted">Avg Cost/Req</div>
          </div>
        </Card>
      </div>

      {/* Menu Items */}
      <div className="space-y-2">
        <Card 
          className="bg-gray-900 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => handleViewChange('mcp-servers')}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success-600/20 rounded-lg">
                <Server className="w-5 h-5 text-success-600" />
              </div>
              <div>
                <div className="font-medium text-console-text">MCP Servers</div>
                <div className="text-xs text-console-muted">Manage protocol servers</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-console-muted" />
          </div>
        </Card>

        <Card 
          className="bg-gray-900 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => handleViewChange('ai-models')}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-600/20 rounded-lg">
                <Brain className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <div className="font-medium text-console-text">AI Models</div>
                <div className="text-xs text-console-muted">Monitor model performance</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-console-muted" />
          </div>
        </Card>

        <Card 
          className="bg-gray-900 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => handleViewChange('orchestration')}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-secondary-600/20 rounded-lg">
                <Users className="w-5 h-5 text-secondary-600" />
              </div>
              <div>
                <div className="font-medium text-console-text">Agent Orchestration</div>
                <div className="text-xs text-console-muted">Distributed agent management</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-console-muted" />
          </div>
        </Card>

        <Card 
          className="bg-gray-900 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => handleViewChange('security')}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning-600/20 rounded-lg">
                <Shield className="w-5 h-5 text-warning-600" />
              </div>
              <div>
                <div className="font-medium text-console-text">MCP Security</div>
                <div className="text-xs text-console-muted">Security gateway controls</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-console-muted" />
          </div>
        </Card>

        <Card 
          className="bg-gray-900 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => handleViewChange('compliance')}
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent-600/20 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-accent-600" />
              </div>
              <div>
                <div className="font-medium text-console-text">Compliance</div>
                <div className="text-xs text-console-muted">Monitor compliance status</div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-console-muted" />
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-gray-900">
        <div className="p-4">
          <h3 className="text-sm font-medium text-console-text mb-3">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Settings className="w-4 h-4" />
              Configure
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Eye className="w-4 h-4" />
              View Logs
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" />
              Security
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderMCPServers = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-console-text">MCP Servers</h2>
        <Button variant="primary" size="sm">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {mcpServers.map((server) => (
        <Card key={server.id} className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-2 h-2 rounded-full',
                  server.status === 'running' ? 'bg-success-500' :
                  server.status === 'error' ? 'bg-danger-500' :
                  server.status === 'stopped' ? 'bg-gray-500' :
                  'bg-warning-500',
                )} />
                <h3 className="font-medium text-console-text text-sm">{server.name}</h3>
              </div>
              <Badge variant={getRiskBadgeVariant(server.riskLevel)} className="text-xs">
                {server.riskLevel}
              </Badge>
            </div>

            <div className="text-xs text-console-muted mb-3">
              v{server.version} • {server.deployment}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-xs text-console-muted">Requests</div>
                <div className="text-sm font-medium text-console-text">
                  {server.requestCount.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-console-muted">Response</div>
                <div className="text-sm font-medium text-console-text">{server.responseTime}ms</div>
              </div>
              <div>
                <div className="text-xs text-console-muted">Error Rate</div>
                <div className="text-sm font-medium text-console-text">{server.errorRate}%</div>
              </div>
              <div>
                <div className="text-xs text-console-muted">Memory</div>
                <div className="text-sm font-medium text-console-text">{server.memoryUsage}%</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-1">
              {server.capabilities.slice(0, 3).map((cap) => (
                <Badge key={cap} variant="secondary" className="text-xs">
                  {cap}
                </Badge>
              ))}
              {server.capabilities.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{server.capabilities.length - 3}
                </Badge>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  const renderAIModels = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-console-text">AI Models</h2>
        <Button variant="primary" size="sm">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {aiModels.map((model) => (
        <Card key={model.id} className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={cn(
                  'w-2 h-2 rounded-full',
                  model.status === 'active' ? 'bg-success-500' :
                  model.status === 'error' ? 'bg-danger-500' :
                  model.status === 'idle' ? 'bg-gray-500' :
                  'bg-warning-500',
                )} />
                <h3 className="font-medium text-console-text text-sm">{model.name}</h3>
              </div>
              <Badge variant={getRiskBadgeVariant(model.riskLevel)} className="text-xs">
                {model.riskLevel}
              </Badge>
            </div>

            <div className="text-xs text-console-muted mb-3">
              {model.provider} • {model.deployment}
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-xs text-console-muted">Tokens</div>
                <div className="text-sm font-medium text-console-text">
                  {model.tokensProcessed.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-console-muted">Latency</div>
                <div className="text-sm font-medium text-console-text">{model.averageLatency}ms</div>
              </div>
              <div>
                <div className="text-xs text-console-muted">Cost/Req</div>
                <div className="text-sm font-medium text-console-text">${model.costPerRequest}</div>
              </div>
              <div>
                <div className="text-xs text-console-muted">Last Used</div>
                <div className="text-sm font-medium text-console-text">
                  {formatRelativeTime(model.lastUsed)}
                </div>
              </div>
            </div>

            <div className="text-xs text-console-muted">
              Data Residency: {model.dataResidency}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );


  return (
    <div className="min-h-screen bg-console-bg pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-900/90 backdrop-blur-md border-b border-gray-800">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-console-text">AI & MCP</h1>
              <p className="text-xs text-console-muted mt-1">Management Console</p>
            </div>
            <Badge variant="success" className="flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              <span className="text-xs">Operational</span>
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {currentView === 'main' && renderMainView()}
        {currentView === 'mcp-servers' && renderMCPServers()}
        {currentView === 'ai-models' && renderAIModels()}
        {currentView === 'orchestration' && <MobileDistributedAgentOrchestration />}
        {currentView === 'security' && <MobileMCPSecurityGateway />}
        {currentView === 'compliance' && <MobileComplianceMonitor />}
      </div>

      {/* Back Button for Sub-views */}
      {currentView !== 'main' && (
        <div className="fixed bottom-20 left-0 right-0 p-4 bg-gray-900/90 backdrop-blur-md border-t border-gray-800">
          <Button 
            variant="secondary" 
            className="w-full"
            onClick={() => handleViewChange('main')}
          >
            Back to AI & MCP Overview
          </Button>
        </div>
      )}
    </div>
  );
};